import { config as appConfig } from '@/core'
import { FormOfAddress, FREE_LANGUAGE_TAGS } from './types'

export function debounce<T extends unknown[], U>(callback: (...args: T) => PromiseLike<U> | U, wait: number) {
  let timer: ReturnType<typeof setTimeout>

  return (...args: T): Promise<U> => {
    clearTimeout(timer)
    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(callback(...args)), wait)
    })
  }
}

export const getAmplitudeApiKey = () => {
  switch (appConfig.app.env) {
    case 'local':
      return process.env.AMPLITUDE_LOCAL_API_KEY
    case 'test':
      return process.env.AMPLITUDE_STAGE_API_KEY
    case 'production':
      return process.env.AMPLITUDE_PROD_API_KEY
    default:
      console.error('Unrecognized Amplitude Environment')
  }
}

export function getLanguageLabel(tag: string | undefined, language: string): string {
  if (!tag) return '–'

  try {
    return (
      new Intl.DisplayNames(language, {
        type: 'language',
        languageDisplay: 'dialect',
        fallback: 'code',
      }).of(tag) ?? tag
    )
  } catch {
    return tag
  }
}

export const isFormalityApplicable = (form_of_address: string): boolean => {
  return form_of_address.toLowerCase() !== FormOfAddress.NOT_APPLICABLE
}

export const isFreeLanguageTag = (tag: string): boolean => (FREE_LANGUAGE_TAGS as readonly string[]).includes(tag)

const MAINTENANCE_FLAG = 'maintenance-in-progress'
const MAINTENANCE_CONTEXT = { kind: 'app', key: 'laas-ui' }

/**
 * Whether LaunchDarkly's `maintenance-in-progress` kill switch is on, read for the whole app (the
 * `app` context), not per tenant. It runs in index.tsx before React mounts, so it calls
 * LaunchDarkly's evaluation endpoint directly instead of going through useFeatureFlag. Any failure,
 * and local development, reads as "no maintenance" so a LaunchDarkly outage never locks users out.
 */
export const isMaintenanceMode = async (): Promise<boolean> => {
  if (appConfig.app.env === 'local') return false

  try {
    const { clientSideId } = appConfig.launchDarkly
    const response = await fetch(
      `https://clientsdk.launchdarkly.com/sdk/evalx/${clientSideId}/contexts/${btoa(JSON.stringify(MAINTENANCE_CONTEXT))}`,
      {
        method: 'GET',
        headers: {
          Authorization: clientSideId,
          'Content-Type': 'application/json',
        },
      },
    )

    if (!response.ok) return false

    const data = await response.json()
    return Boolean(data[MAINTENANCE_FLAG]?.value)
  } catch (error) {
    console.error('LaunchDarkly maintenance check failed:', error)
    return false
  }
}
